import React from 'react';
import { useTranslation } from 'react-i18next';
import { type NavItemNoIcon, type SiteConfig } from '@cx-web/domain-types';

import { useMenuOverflow } from '../../hooks/use-menu-overflow';
import { HeaderNavItem } from '../HeaderNavItem/index';
import MoreDropdown from '../MoreDropdown/index';

import styles from './TertiaryHeaderMenuItems.module.css';

type TertiaryHeaderMenuItemsProps = {
  bookmakers: SiteConfig['bookmakers'];
  dropdownRef: React.RefObject<HTMLDivElement | null>;
  links: NavItemNoIcon[];
  serverUrl: string;
};

// Tertiary (L3) menu for the new design. Mirrors PrimaryHeaderLeftMenuItems: shows the items that fit
// and collapses the overflow into MoreDropdown (widths measured via the hidden copy below).
const TertiaryHeaderMenuItems = ({ bookmakers, dropdownRef, links, serverUrl }: TertiaryHeaderMenuItemsProps) => {
  const { t } = useTranslation(['label']);
  const { containerRef, dropdownItems, mainItems, measureRef, showMoreDropdown } = useMenuOverflow(links);

  return (
    <div className={styles.wrapper} data-testid="tertiary-header-menu" ref={containerRef}>
      {/* Plain list — no role="toolbar": links have no roving-tabindex, and toolbar orphans the <li>s. */}
      <ul aria-label="Tertiary navigation" className={styles.navItems} data-testid="tertiary-header-nav-items">
        {mainItems.map((link, index) => (
          <HeaderNavItem
            {...link}
            bookmakers={bookmakers}
            dropdownRef={dropdownRef}
            isUkStyle
            key={`${link.link}-${index}`}
            serverUrl={serverUrl}
            variant="tertiary"
          />
        ))}
      </ul>

      {showMoreDropdown && <MoreDropdown items={dropdownItems} serverUrl={serverUrl} variant="tertiary" />}

      {/* Hidden width-measurement copy of the full list + More trigger (visibility:hidden, not announced). */}
      <div className={styles.measureClip}>
        <ul aria-hidden className={styles.measure} data-testid="tertiary-header-nav-measure" ref={measureRef}>
          {links.map((link, index) => (
            <HeaderNavItem
              {...link}
              bookmakers={bookmakers}
              dropdownRef={dropdownRef}
              isUkStyle
              key={`${link.link}-${index}`}
              serverUrl={serverUrl}
              variant="tertiary"
            />
          ))}
          <li className={styles.measureMore}>{t('RESPONSIVE_MORE')}</li>
        </ul>
      </div>
    </div>
  );
};

export { TertiaryHeaderMenuItems };
